// ── Email Category Types ──────────────────────────────────────────────────────

export type EmailCategory = 'all' | 'white-listed' | 'grey-listed' | 'black-listed';

export interface EmailCounts {
  total: number;
  completed: number;
  pending: number;
  rejected: number;
}

export type DateRange = 'today' | 'week' | 'month';

export type GreylistFilter = 'ALL' | 'DOMAIN' | 'INFO';

export const INTENT_GROUPS = ['COMPLAINT', 'FEEDBACK', 'APPEAL', 'STATUS_CHECK', 'FAQ'] as const;

export const BLACKLIST_REASONS = [
  { value: 'SPAM',        labelKey: 'EC_REASON_SPAM' },
  { value: 'IRRELEVANT',  labelKey: 'EC_REASON_IRRELEVANT' },
  { value: 'PROMOTIONAL', labelKey: 'EC_REASON_PROMOTIONAL' },
] as const;

export interface EmailFilters {
  from?: string;           // ISO-8601 Instant
  to?: string;             // ISO-8601 Instant
  search?: string;
  intentGroup?: string;    // COMPLAINT | FEEDBACK | APPEAL | STATUS_CHECK | FAQ
  needsReview?: boolean;   // Pending + Completed
  greylistType?: GreylistFilter;  // Pending only
  reason?: string;         // SPAM | IRRELEVANT | PROMOTIONAL — Rejected only
}
export type ParsingStatus = 'PENDING_INFO' | 'COMPLETE';
export type GreylistType = 'DOMAIN' | 'INFO' | null;
export type RegistrationStatus = 'REGISTERED' | 'ALREADY_REGISTERED' | 'FAILED' | null;
export type ReplyStatus = 'SENT' | 'FAILED' | 'CITIZEN_REPLIED' | 'MAX_RETRIES_EXCEEDED' | null;

// ── List API DTOs ─────────────────────────────────────────────────────────────

/** Matches backend EmailListItemDto / BlacklistItemDto (unified) */
export interface EmailListItem {
  draftId?: number;
  intakeId: number;
  /** Explicit category from backend — present on /all, /whitelisted, /greylisted rows */
  category?: Exclude<EmailCategory, 'all'>;
  senderEmail: string;
  senderDisplayName: string;
  subject: string;
  bodySnippet: string | null;
  receivedAt: string;
  lastActivityAt?: string;
  status: string;
  parsingStatus?: ParsingStatus;
  greylistType?: GreylistType;
  senderCategory?: string;
  intentGroup?: string;
  needsReview?: boolean;
  replyCount?: number;
  missingFields?: string | null;
  name?: string;
  cnic?: string;
  mobile?: string;
  district?: string;
  tehsil?: string;
  registrationStatus?: RegistrationStatus;
}

/** Matches backend PagedResponse<T> */
export interface PagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// ── Thread API DTO ────────────────────────────────────────────────────────────

/** Matches backend EmailThreadDto — GET /api/v1/emails/{draftId}/thread */
export interface EmailThread {
  draftId: number;
  parsingStatus: ParsingStatus;
  greylistType: GreylistType;
  senderCategory: string;
  needsReview: boolean;
  missingFields: string | null;
  intentName: string;
  intentGroup: string;
  aiConfidence: number;
  name: string | null;
  cnic: string | null;
  mobile: string | null;
  gender: string | null;
  districtName: string | null;
  tehsilName: string | null;
  mouzaName: string | null;
  address: string | null;
  messages: ThreadMessage[];
}

/** Individual message within an email thread */
export interface ThreadMessage {
  intakeId: number;
  type: 'ORIGINAL' | 'REPLY';
  senderEmail: string;
  senderDisplayName: string;
  subject: string;
  bodyText: string;
  receivedAt: string;
}

// ── Draft Detail API DTO ──────────────────────────────────────────────────────

/** Matches backend DraftDetailDto — GET /api/v1/emails/draft?intakeId={id} */
export interface DraftDetail {
  draftId: number;
  intakeId: number;
  messageId: string;
  senderEmail: string;
  senderDisplayName: string;
  subject: string;
  sentDate: string;
  receivedAt: string;
  intakeStatus: string;
  parsingStatus: ParsingStatus;
  greylistType: GreylistType;
  senderCategory: string;
  needsReview: boolean;
  missingFields: string | null;
  bufferToken: string;
  createdAt: string;
  expiresAt: string;
  intentName: string;
  intentGroup: string;
  aiConfidence: number;
  name: string | null;
  cnic: string | null;
  mobile: string | null;
  gender: string | null;
  districtName: string | null;
  tehsilName: string | null;
  mouzaName: string | null;
  address: string | null;
  divisionId: number | null;
  districtId: number | null;
  tehsilId: number | null;
  mouzaId: number | null;
  complaintCategoryId: number | null;
  complaintCategoryName: string | null;
  subCategoryId: number | null;
  subCategoryName: string | null;
  complaintDetail: string | null;
  complaintAddress: string | null;
  complainantAddress: string | null;
  body: string;
  complaintNo: string | null;
  complaintId: number | null;
  registrationStatus: RegistrationStatus;
}

// ── Admin Submit ──────────────────────────────────────────────────────────────

export interface AdminComplaintSubmitRequest {
  name?: string | null;
  cnic?: string | null;
  mobile?: string | null;
  gender?: string | null;
  divisionId?: number | null;
  districtId?: number | null;
  tehsilId?: number | null;
  mouzaId?: number | null;
  districtName?: string | null;
  tehsilName?: string | null;
  mouzaName?: string | null;
  address?: string | null;
  complaintAddress?: string | null;
  complaintCategoryId?: number | null;
  complaintCategoryName?: string | null;
  subCategoryId?: number | null;
  subCategoryName?: string | null;
  complaintDetail?: string | null;
}

export interface AdminComplaintSubmitResponse {
  draftId: number;
  complaintNo: string | null;
  complaintId: number | null;
  registrationStatus: 'REGISTERED' | 'ALREADY_REGISTERED' | 'FAILED' | 'VALIDATION_FAILED';
  message: string;
}

// ── Attachment ────────────────────────────────────────────────────────────────

/** Matches backend AttachmentDto — GET /api/v1/emails/intake/{intakeId}/attachments */
export interface EmailAttachment {
  attachmentId: number;
  fileName: string;
  mimeType: string;
  fileSize: number | null;
}

// ── Admin Reply ───────────────────────────────────────────────────────────────

/** Matches backend AdminReplyResponse — POST /api/v1/emails/admin/intake/{intakeId}/reply */
export interface AdminReplyResponse {
  sent: boolean;
  replyIntakeId: number;
  message: string;
}

/** Matches backend AdminRejectResponse — POST /api/v1/emails/admin/intake/{intakeId}/reject */
export interface AdminRejectResponse {
  intakeId: number;
  status: string;   // SPAM | IRRELEVANT | PROMOTIONAL
  message: string;
}
